import { faGlobe } from '@fortawesome/pro-solid-svg-icons/faGlobe';
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser';
import { Type } from '@sinclair/typebox';
import { Value } from '@sinclair/typebox/value';
import { createFileRoute } from '@tanstack/react-router';
import { useFlagsStatus } from '@unleash/proxy-client-react';
import type { FC } from 'react';
import { Stack } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';

import { AuthState, ReportError, useAuth, useMutation, useQuery } from '@furality/frontend-stack';
import { Button } from '@furality/furality-purpose';
import fox from '#/api/fox.ts';
import FontAwesomeIcon from '#/components/FontAwesomeIcon.tsx';
import ErrorCard from '@/components/_status/ErrorCard.tsx';
import SuccessCard from '@/components/_status/SuccessCard.tsx';
import Avatar from '@/components/_world-link/Avatar.tsx';
import Footer from '@/components/_world-link/Footer.tsx';
import ErrorDisplay from '@/components/ErrorDisplay.tsx';
import LinkButton from '@/components/LinkButton.tsx';
import { CardLayout } from '@/components/layouts/index.tsx';
import LoadingOverlay from '@/components/loading/LoadingOverlay.tsx';
import PageLoading from '@/components/loading/PageLoading.tsx';
import RequireLoggedIn from '@/components/RequireLoggedIn.tsx';
import QUERY_KEYS from '@/lib/QUERY_KEYS.ts';

const ConnectionDots = () => (
  <Stack direction="horizontal" gap={2} className="mx-3">
    <Dot />
    <Dot />
    <Dot />
  </Stack>
);

const Dot: FC = () => (
  <div
    className="rounded-circle"
    style={{
      content: '',
      width: 10,
      height: 10,
      background: 'rgba(255, 255, 255, 0.5)',
    }}
  />
);

const LinkWorld = () => {
  const { t, ready: translationReady } = useTranslation(['common', 'link_world']);
  const auth = useAuth();

  const featuresReady = useFlagsStatus().flagsReady;

  const { worldToken } = Route.useSearch();

  const getLinkStatus = useQuery({
    queryKey: [QUERY_KEYS.VrchatWorldLink, worldToken],
    queryFn: async () => {
      if (!worldToken) {
        return;
      }
      const res = await fox.value.vRChatWorldAuthGetLinkStatus({ params: { worldToken } });
      switch (res.status) {
        case 200:
          return res.data;
        default: {
          throw new ReportError(res);
        }
      }
    },
    enabled: !!worldToken,
  });

  const handleLinkWorld = useMutation({
    mutationFn: async () => {
      const res = await fox.value.vRChatWorldAuthLink({
        body: { worldToken },
      });
      switch (res.status) {
        case 204: {
          return;
        }
        default: {
          throw new ReportError(res);
        }
      }
    },
  });

  if (auth.state === AuthState.Error) {
    return (
      <div className="flex-center h-100 flex-grow-1 min-vh-100">
        <ErrorDisplay title={t('link_world:link_world.sign_in_error')} error={auth.error} />
      </div>
    );
  }

  if (!featuresReady || auth.state === AuthState.Loading || !translationReady) {
    return <PageLoading />;
  }

  const isSuccess = getLinkStatus.data?.status === 'CONNECTED' || handleLinkWorld.isSuccess;
  const isError = getLinkStatus.data?.status === 'INVALID' || handleLinkWorld.isError;

  return (
    <RequireLoggedIn>
      <Stack gap={5} className="mt-5 align-items-center">
        <Stack className="justify-content-center" direction="horizontal" gap={5}>
          <FontAwesomeIcon icon={faGlobe} size="6x" />
          <ConnectionDots />
          {auth.user && auth.user.picture ? (
            <Avatar size={100} src={auth.user.picture} alt={auth.user.vrchatDisplayName} />
          ) : (
            <FontAwesomeIcon icon={faUser} size="6x" />
          )}
        </Stack>

        <Stack gap={2} className="text-body align-items-center">
          <Trans
            t={t}
            i18nKey="link_world:link_world.header"
            components={{
              h: <h1 className="h3 fw-bold my-0" />,
              p: <p className="my-0" />,
            }}
          />
          <div className="text-center align-items-center">
            {auth.user && (
              <p className="my-0">
                <Trans
                  t={t}
                  i18nKey="link_world:link_world.body"
                  values={{ displayName: auth.user.vrchatDisplayName }}
                />
              </p>
            )}
          </div>
        </Stack>

        <div className="position-relative w-100 px-6">
          {handleLinkWorld.isPending && <LoadingOverlay />}
          {isError ? (
            <div className="d-grid">
              <ErrorCard>{t('link_world:link_world.error')}</ErrorCard>
              <Button className="me-0" onClick={() => handleLinkWorld.mutate()}>
                {t('common:retry')}
              </Button>
            </div>
          ) : isSuccess ? (
            <div className="d-grid">
              <SuccessCard>{t('link_world:link_world.success')}</SuccessCard>
              <LinkButton to="/explore/dealers" className="me-0">
                {t('link_world:link_world.continue')}
              </LinkButton>
            </div>
          ) : (
            <Stack gap={4} className="d-grid">
              <Button
                disabled={!worldToken}
                className="me-0"
                onClick={() => handleLinkWorld.mutate()}
              >
                {t('link_world:link_world.allow_access')}
              </Button>
              <LinkButton to="/profile" variant="outline-danger">
                {t('common:shared.cancel')}
              </LinkButton>
            </Stack>
          )}
        </div>
        <Footer />
      </Stack>
    </RequireLoggedIn>
  );
};

const LinkWorldSearch = Type.Object({
  worldToken: Type.String(),
});
export const Route = createFileRoute('/link-world')({
  component: () => (
    <CardLayout title="shared.navigation.link_world">
      <LinkWorld />
    </CardLayout>
  ),
  validateSearch: (search) => Value.Cast(LinkWorldSearch, search),
});
